import { feedbackAudioUrl, isCorrectFeedback } from '../feedbackAudio';
import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import { GardenBackdrop } from '../components/RoundView';
import { withTurkishGenitive } from '../turkishSuffixes';
import { recoverLetterHuntRound, saveLetterHuntProgress } from '../miniTasksApi';
import SingleStoreyA from './SingleStoreyA';
import SchoolN from './SchoolN';
import Grade1Letter from './Grade1Letter';
import './soundHunt.css';
import './letterHunt.css';
import useTourIdleHelp from './useTourIdleHelp';
import { setRoundAudioPlayback } from './pronunciationPlayback';

const INSTRUCTION = 'i_l1_g2_instruction';
function Symbol({ name }) {
  const paths = {
    star: <path d="M50 8L62 35L92 38L70 58L77 88L50 72L23 88L30 58L8 38L38 35Z" />,
    circle: <circle cx="50" cy="50" r="34" />,
    heart: <path d="M50 85C-15 45 17 5 50 30C83 5 115 45 50 85Z" />,
    triangle: <path d="M50 12L90 84H10Z" />,
    cloud: <path d="M25 78C-1 78 0 43 24 42C22 8 69 4 77 36C105 34 108 78 78 78Z" />,
  };
  return <svg viewBox="0 0 100 100" aria-hidden="true" fill="currentColor">{paths[name]}</svg>;
}

export default function LetterHuntRoundView({ round, boardSessionId, classTitle, soundEnabled = true,
  onToggleSound, onComplete, onExit, sharedAudioRef, onNextRound, localProgress, idleHelpDisabled = false }) {
  const [phase, setPhase] = useState('loading');
  const [found, setFound] = useState([]);
  const [shaking, setShaking] = useState(null);
  const [error, setError] = useState('');
  const [wrongFeedback, setWrongFeedback] = useState(0);
  const isL4G02 = round.source_level === 4 && round.game_id === 'G02';
  const hasL4Capsule = isL4G02 || (round.source_level === 4 && round.game_id === 'G04');
  const isN = round.target_letter === 'n';
  const TargetGlyph = ['e', 't', 'i', 'l'].includes(round.target_letter)
    ? () => <Grade1Letter letter={round.target_letter} /> : isN ? SchoolN : SingleStoreyA;
  const localAudioRef = useRef(null);
  const audioRef = sharedAudioRef || localAudioRef;
  const muted = useRef(!soundEnabled);
  muted.current = !soundEnabled;
  const handlers = useRef({});
  useTourIdleHelp(round.v22 && phase === 'ready' && !idleHelpDisabled, () => handlers.current.accept('help_prompt'));
  const complete = useRef(onComplete);
  complete.current = onComplete;
  useEffect(() => { if (audioRef.current) audioRef.current.muted = !soundEnabled; }, [audioRef, soundEnabled]);

  useEffect(() => {
    let alive = true, running = false, locked = true, cancelAudio, cancelShake, retry;
    let current = round, events = [], origin = 0, prepared = false;
    const targets = new Set(round.items.filter((item) => item.is_target).map((item) => item.id));
    const selected = () => events.filter((event) => event.kind === 'select' && targets.has(event.item_id)).map((event) => event.item_id);
    const elapsed = () => Math.max(events.at(-1)?.elapsed_ms || 0, Date.now() - origin);
    const append = (kind, item_id = null) => { events = [...events, { kind, item_id, elapsed_ms: elapsed() }]; };
    const playOnce = (id) => new Promise((resolve, reject) => {
      const url = feedbackAudioUrl(id, round.audio[id]);
      if (!alive || !url) { reject(new Error('Ses kaydı bulunamadı.')); return; }
      const audio = audioRef.current || new Audio(url);
      audioRef.current = audio;
      audio.src = url; audio.preload = 'auto'; audio.muted = muted.current;
      setRoundAudioPlayback(audio, round, id);
      let settled = false;
      const clean = () => { clearTimeout(timer); audio.onended = null; audio.onerror = null; cancelAudio = null; };
      const fail = (err) => { if (settled) return; settled = true; clean(); audio.pause(); reject(err); };
      const timer = setTimeout(() => fail(new Error('Ses yüklenemedi. Yeniden deneyelim.')), isCorrectFeedback(id) ? 1500 : 30000);
      audio.onended = () => { if (settled) return; settled = true; clean(); resolve(); };
      audio.onerror = () => fail(new Error('Ses yüklenemedi. Yeniden deneyelim.'));
      cancelAudio = () => fail(new Error('cancelled'));
      try { Promise.resolve(audio.play()).catch(fail); } catch (err) { fail(err); }
    });
    const play = (id) => playOnce(id).catch((err) => {
      if (!alive) throw err;
      if (isCorrectFeedback(id)) return;
      if (!round.v22) throw err;
      return playOnce(id);
    });
    const save = async () => {
      if (round.local_tour) return localProgress(round.game_id, events);
      if (!round.preview) return saveLetterHuntProgress(round.round_id, boardSessionId, events);
      const taps = events.filter((event) => event.kind === 'select');
      const completed = selected().length === round.target_count;
      return { round_completed: completed, summary: { preview: true, task_type: 'letter_hunt', completed,
        first_action_correct: taps.length ? targets.has(taps[0].item_id) : null, target_taps: selected().length,
        incorrect_taps: taps.filter((event) => !targets.has(event.item_id)).length,
        completion_time_ms: completed ? events.at(-1).elapsed_ms : null,
        replay_instruction_count: events.filter((event) => event.kind === 'instruction_replay').length,
        help_prompt_count: events.filter((event) => event.kind === 'help_prompt').length, rounds_remaining: 1 },
        next_round: completed ? round.next_round : null };
    };
    const run = async (operation) => {
      if (running || !alive) return;
      running = true; locked = true; setPhase('playing'); setError('');
      retry = () => run(operation);
      try { await operation(); }
      catch (err) {
        if (alive) { setError(err.name === 'NotAllowedError'
          ? (round.v22 ? 'Ses başlayamadı. Tekrar Dene düğmesine dokun.'
            : 'Ses başlayamadı. Yönergeyi dinleyelim düğmesine yeniden dokun.') : err.message); setPhase('retry'); }
      } finally { running = false; }
    };
    const ready = () => { if (alive) { locked = false; setShaking(null); setPhase('ready'); } };
    const finish = async (summary, nextRound = current.next_round) => {
      if (!alive) return;
      setPhase('finishing');
      if (!round.skip_completion_speech) await play(round.v22 ? round.complete_audio_id : 'f_l1_g2_complete');
      if (alive) {
        if (nextRound && onNextRound) onNextRound(nextRound);
        else complete.current(summary);
      }
    };
    const start = () => run(async () => {
      if (current.status === 'completed') { await finish(current.summary); return; }
      await play(round.v22 ? round.intro_audio_id : round.continuing ? 'i_l1_g2_continue' : INSTRUCTION);
      if (!alive) return;
      if (!events.length) { origin = Date.now(); events = [{ kind: 'intro_completed', item_id: null, elapsed_ms: 0 }]; }
      await save(); ready();
    });
    const accept = (kind, item) => {
      if (locked || (item && selected().includes(item.id))) return;
      setWrongFeedback(count => kind === 'select' && !item.is_target ? count + 1 : 0);
      locked = true;
      if (kind !== 'help_prompt' || !round.unmeasured_help) append(kind, item?.id || null);
      const firstTarget = kind === 'select' && item.is_target && selected().length === 1;
      const helpNeeded = !round.v22 && kind === 'select' && !item.is_target
        && events.filter((event) => event.kind === 'select' && !targets.has(event.item_id)).length >= 2
        && !events.some((event) => event.kind === 'help_prompt');
      const shakeDone = kind === 'select' && !item.is_target ? new Promise((resolve) => {
        const timer = setTimeout(() => { cancelShake = null; resolve(); }, 300);
        cancelShake = () => { clearTimeout(timer); resolve(); };
      }) : Promise.resolve();
      if (kind === 'select') { if (item.is_target) setFound(selected()); else setShaking(item.id); }
      let saved = false, played = false, helpSaved = false, result;
      run(async () => {
        if (!saved) { result = await save(); saved = true; }
        if (!alive) return;
        if (!played) {
          if (kind === 'help_prompt') await play(round.help_audio_id);
          else if (kind === 'instruction_replay') await play(round.v22 ? round.intro_audio_id : INSTRUCTION);
          else if (item.is_target) await play(firstTarget ? (round.v22 ? round.first_correct_audio_id : 'f_l1_g2_first_correct') : 's_l1_g2_pop');
          else if (helpNeeded) await play('i_l1_g2_help');
          else if (round.wrong_audio_id && (round.final_step || round.v22)) await play(round.wrong_audio_id);
          played = true;
          if (helpNeeded && alive) append('help_prompt');
        }
        if (!alive) return;
        if (helpNeeded && !helpSaved) { await save(); helpSaved = true; }
        if (result.round_completed) await finish(result.summary, result.next_round || current.next_round);
        else { await shakeDone; ready(); }
      });
    };
    handlers.current = { start, accept, retry: () => retry?.() };
    run(async () => {
      if (!round.preview && !round.local_tour) current = await recoverLetterHuntRound(round.round_id, boardSessionId);
      if (!alive) return;
      events = current.progress?.events || [];
      setFound(current.progress?.selected_target_ids || []);
      origin = current.progress?.selection_opened_at ? Date.parse(current.progress.selection_opened_at)
        : Date.now() - (events.at(-1)?.elapsed_ms || 0);
      prepared = true;
      setPhase('waiting');
    }).then(() => { if (alive && prepared && round.continuing) start(); });
    return () => { alive = false; locked = true; cancelAudio?.(); cancelShake?.(); audioRef.current?.pause(); };
  }, [round, boardSessionId, audioRef, onNextRound, localProgress]);

  return <Box className={`sound-hunt letter-hunt ${isL4G02 ? 'letter-hunt-l4-g02' : ''} ${hasL4Capsule ? 'l4-hunt-feedback' : ''} ${phase === 'finishing' ? 'is-complete' : ''}`}>
    <GardenBackdrop />
    <Box className="sound-hunt-content">
      <MiniTaskGameHeader classTitle={classTitle} soundEnabled={soundEnabled} onToggleSound={onToggleSound} soundRequired={round.v22} />
      <Box className="sound-hunt-header">
        <Button className="sound-hunt-home" onClick={onExit} startIcon={<ArrowBackRoundedIcon />}>Ana sayfa</Button>
        <Typography component="h1" className="sound-hunt-title">{withTurkishGenitive(round.student?.display_name || 'Öğrenci')} Mini Görevi</Typography>
        <Box aria-label={round.progress_text ? `Meydan okuma ${round.progress_text}` : `${found.length} hedef bulundu`} className="sound-hunt-progress">
          {round.progress_text || [0, 1, 2, 3].map((i) => <span key={i} className={i < found.length ? 'found' : ''}>★</span>)}
        </Box>
      </Box>
      <Box className="sound-hunt-bottom">
        <Typography className="sound-hunt-badge">{round.badge_title || `⭐ ${isN ? 'N' : 'A'} Harflerini Yakala`}</Typography>
        {phase === 'finishing' && <Box role="img" className="letter-hunt-final" aria-label={`${round.target_letter || (isN ? 'n' : 'a')} harfi`}><TargetGlyph /></Box>}
        <Typography role="status" aria-live="polite" aria-atomic="true" className="sound-hunt-status">
          {error || (wrongFeedback > 0 && <span key={wrongFeedback} className={hasL4Capsule ? 'letter-hunt-wrong-capsule' : undefined}><CloseRoundedIcon aria-hidden="true" /> Bir daha deneyelim.</span>)}
        </Typography>
        <Box className="letter-hunt-grid" data-item-count={round.items.length}>
          {round.items.map((item, i) => <button key={item.id} type="button" aria-label={`${i + 1}. öğeyi seç`}
            className={`letter-hunt-item ${found.includes(item.id) ? 'is-found' : ''} ${shaking === item.id ? 'is-shaking' : ''}`}
            disabled={phase !== 'ready' || found.includes(item.id)} onClick={() => handlers.current.accept('select', item)}>
            {item.is_target ? <TargetGlyph /> : item.letter ? <Grade1Letter letter={item.letter} />
              : item.glyph === 'single-storey-a' ? <SingleStoreyA /> : <Symbol name={item.symbol} />}
            {found.includes(item.id) && <span className="letter-hunt-check" aria-hidden="true">✓</span>}
          </button>)}
        </Box>
        <Box className="sound-hunt-controls">
          <Button className="sound-hunt-control" disabled={!['waiting', 'ready', 'retry'].includes(phase)}
            startIcon={<RecordVoiceOverRoundedIcon />} onClick={() => phase === 'waiting' ? handlers.current.start()
              : phase === 'retry' ? handlers.current.retry() : handlers.current.accept('instruction_replay')}>
            {round.v22 && phase === 'retry' ? 'Tekrar Dene' : 'Yönergeyi dinleyelim'}
          </Button>
        </Box>
      </Box>
    </Box>
  </Box>;
}
